/**
 * Media V2 Kasası — Soru Detay Entegrasyonu
 *
 * Özellikler:
 *  - T/R/TR/ZTR/V kalite uyarıları
 *  - Dinamik question/option lane'leri (usage_key + option_key)
 *  - Alternatif gruplaması + revizyon geçmişi
 *  - V1 legacy medya + V2 binding yan yana önizleme
 *  - Aktif selection rozeti
 *  - Upload oturumu akışı (backend presigned URL API)
 *  - Dış URL kaydı
 *  - Yayın seçimi değiştirme (ayrı onay modalı)
 *  - İzin-bilinçli UI (upload_media / publish_question_media)
 *  - Erişilebilir Türkçe etiketler
 */
import { useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";
import { toast } from "sonner";
import {
  Upload, Link, ChevronDown, ChevronUp, Image, Play,
  Volume2, FileText, AlertTriangle, Info, CheckCircle,
  Lock, RefreshCw, X, Eye, Layers
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

// ─── Lane tanımları ──────────────────────────────────────────────────────────
const QUESTION_LANES = [
  { usageKey: "question.primary_visual",         label: "Soru Görseli",           v1Field: "question_image_url",              mediaIcon: "image" },
  { usageKey: "question.prompt_video",           label: "Soru Videosu",           v1Field: "question_video_url",              mediaIcon: "video" },
  { usageKey: "question.gif",                    label: "Soru GIF",               v1Field: "question_gif_url",                mediaIcon: "image" },
  { usageKey: "question.animation",              label: "Animasyon",              v1Field: "animation_video_url",             mediaIcon: "video" },
  { usageKey: "question.explanation_video",      label: "Açıklama Videosu",       v1Field: "explanation_video_horizontal_url",mediaIcon: "video" },
  { usageKey: "question.explanation_visual",     label: "Açıklama Görseli",       v1Field: "explanation_image_url",           mediaIcon: "image" },
  { usageKey: "question.correct_feedback_video", label: "Doğru Cevap Videosu",    v1Field: null,                              mediaIcon: "video" },
  { usageKey: "question.wrong_feedback_video",   label: "Yanlış Cevap Videosu",   v1Field: null,                              mediaIcon: "video" },
  { usageKey: "question.audio",                  label: "Ses",                    v1Field: null,                              mediaIcon: "audio" },
  { usageKey: "question.solution_audio",         label: "Çözüm Sesi",             v1Field: null,                              mediaIcon: "audio" },
  { usageKey: "topic.video_short",               label: "Kısa Konu Videosu",      v1Field: "short_video_vertical_url",        mediaIcon: "video" },
  { usageKey: "question.subtitle",               label: "Altyazı",                v1Field: null,                              mediaIcon: "document" },
  { usageKey: "question.interactive",            label: "İnteraktif İçerik",      v1Field: null,                              mediaIcon: "document" },
];

const OPTION_KEYS = ["A", "B", "C", "D"];

const USAGE_KEY_OPTIONS = [
  ...QUESTION_LANES.map(l => ({ value: l.usageKey, label: l.label })),
  { value: "option.visual",    label: "Şık Görseli (option.visual)" },
  { value: "option.feedback",  label: "Şık Geri Bildirimi (option.feedback)" },
];

const ORIENTATION_OPTIONS = [
  { value: "original", label: "Orijinal" },
  { value: "horizontal", label: "Yatay" },
  { value: "vertical", label: "Dikey" },
  { value: "square", label: "Kare" },
];

const MEDIA_TYPE_OPTIONS = [
  { value: "image",       label: "Görsel (image)" },
  { value: "video",       label: "Video (video)" },
  { value: "audio",       label: "Ses (audio)" },
  { value: "gif",         label: "GIF (gif)" },
  { value: "animation",   label: "Animasyon (animation)" },
  { value: "document",    label: "Doküman (document)" },
  { value: "subtitle",    label: "Altyazı (subtitle)" },
  { value: "interactive", label: "İnteraktif (interactive)" },
];

// ─── Compact mode grupları ───────────────────────────────────────────────────
const COMPACT_GROUPS = [
  {
    id: 'question',
    label: 'Soru',
    lanes: ['question.primary_visual', 'question.prompt_video', 'question.gif', 'question.animation'],
  },
  {
    id: 'explanation',
    label: 'Açıklama',
    lanes: ['question.explanation_video', 'question.explanation_visual'],
  },
  {
    id: 'feedback',
    label: 'Geri Bildirim',
    lanes: ['question.correct_feedback_video', 'question.wrong_feedback_video'],
  },
  {
    id: 'audio',
    label: 'Ses',
    lanes: ['question.audio', 'question.solution_audio'],
  },
  {
    id: 'supporting',
    label: 'Destekleyici İçerik',
    lanes: ['topic.video_short', 'question.subtitle', 'question.interactive'],
  },
];

// usage_key → lane tanım haritası (compact mode için)
const LANE_BY_KEY = Object.fromEntries(QUESTION_LANES.map(l => [l.usageKey, l]));

// ─── Yardımcı bileşenler ─────────────────────────────────────────────────────

function SeverityIcon({ severity }) {
  if (severity === "warning") return <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />;
  if (severity === "info") return <Info size={13} className="text-blue-500 shrink-0 mt-0.5" />;
  return <AlertTriangle size={13} className="text-red-500 shrink-0 mt-0.5" />;
}

function MediaPreview({ url, mediaType, label, compact = false }) {
  const [err, setErr] = useState(false);
  if (!url) return null;
  const h = compact ? "h-16" : "h-24";
  if (err) return (
    <div className={`${h} flex items-center justify-center bg-slate-100 rounded text-xs text-slate-400`}>
      Yüklenemedi
    </div>
  );
  if (mediaType === "video" || mediaType === "animation") return (
    <video src={url} controls muted playsInline preload="metadata" className={`${h} w-full object-contain rounded bg-slate-100`} onError={() => setErr(true)} />
  );
  if (mediaType === "audio") return (
    <audio src={url} controls className="w-full" onError={() => setErr(true)} />
  );
  if (mediaType === "image" || mediaType === "gif") return (
    <img src={url} alt={label} className={`${h} w-full object-contain rounded bg-slate-100`} onError={() => setErr(true)} />
  );
  return <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 underline">{url.substring(0, 40)}...</a>;
}

function StatusBadge({ status }) {
  const map = {
    active:     { cls: "bg-emerald-100 text-emerald-700",  label: "Aktif" },
    superseded: { cls: "bg-slate-100 text-slate-500",       label: "Eski" },
    archived:   { cls: "bg-red-50 text-red-400",            label: "Arşiv" },
  };
  const { cls, label } = map[status] || { cls: "bg-slate-100 text-slate-500", label: status };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cls}`}>{label}</span>;
}

// ─── V1 Legacy Medya Kartı ───────────────────────────────────────────────────
function V1LegacyCard({ url, mediaType, label }) {
  const [expanded, setExpanded] = useState(false);
  if (!url) return null;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-semibold">V1 LEGACY</span>
          <span className="text-xs text-slate-500">{label}</span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="text-slate-400 hover:text-slate-600"
          data-testid="v1-expand-btn"
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>
      {expanded && (
        <div className="space-y-2">
          <MediaPreview url={url} mediaType={mediaType} label={label} />
          <p className="text-[10px] text-slate-400 font-mono truncate" title={url}>{url}</p>
        </div>
      )}
    </div>
  );
}

// ─── Revizyon Kartı ──────────────────────────────────────────────────────────
function RevisionCard({ binding, asset, isSelected, onSelect, canPublish }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`border rounded-lg p-3 ${isSelected ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
      data-testid={`binding-card-${binding.binding_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-mono text-slate-500">Rev.{binding.revision_no}</span>
          <StatusBadge status={binding.status} />
          {isSelected && (
            <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5">
              <CheckCircle size={9} /> V2 Seçili
            </span>
          )}
          {binding.title && <span className="text-xs text-slate-600">"{binding.title}"</span>}
        </div>
        <div className="flex items-center gap-1">
          {!isSelected && binding.status === "active" && canPublish && (
            <button
              type="button"
              onClick={() => onSelect(binding)}
              className="text-[10px] px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 transition-colors"
              data-testid={`select-binding-${binding.binding_id}`}
            >
              Seç
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-slate-400 hover:text-slate-600"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-2 space-y-2">
          {asset && (
            <MediaPreview
              url={asset.playback_url || asset.external_url || null}
              mediaType={asset.media_type}
              label={binding.usage_key}
            />
          )}
          <div className="text-[10px] text-slate-400 space-y-0.5">
            {asset?.mime_type && <p>MIME: {asset.mime_type}</p>}
            {asset?.file_size && <p>Boyut: {Math.round(asset.file_size / 1024)} KB</p>}
            <p>Oluşturuldu: {binding.created_at?.substring(0, 10)}</p>
            {binding.created_by && <p>Yükleyen: {binding.created_by}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Alternatif Grup ─────────────────────────────────────────────────────────
function AlternativeGroup({ alternativeNo, alternativeId, bindings, assetMap, activeSelectionId, onSelectBinding, canPublish }) {
  const [expanded, setExpanded] = useState(alternativeNo === 1);
  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`alternative-${alternativeId}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <Layers size={12} className="text-slate-400" />
          <span className="text-xs font-medium text-slate-600">Alternatif {alternativeNo}</span>
          <span className="text-[10px] text-slate-400">{bindings.length} revizyon</span>
          {bindings.some(b => b.binding_id === activeSelectionId) && (
            <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded font-semibold">Seçili</span>
          )}
        </div>
        {expanded ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {bindings.map(b => (
            <RevisionCard
              key={b.binding_id}
              binding={b}
              asset={assetMap[b.asset_id]}
              isSelected={b.binding_id === activeSelectionId}
              onSelect={onSelectBinding}
              canPublish={canPublish}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Lane Panel ──────────────────────────────────────────────────────────────
function LanePanel({ laneKey, label, v1Url, v1MediaType, bindings, assetMap, activeSelectionId, onSelectBinding, onUpload, canUpload, canPublish }) {
  const [expanded, setExpanded] = useState(false);
  const hasContent = v1Url || bindings.length > 0;

  // Alternatif gruplama
  const altMap = {};
  bindings.forEach(b => {
    const key = b.alternative_id || b.binding_id;
    if (!altMap[key]) altMap[key] = { alternativeId: key, alternativeNo: b.alternative_no, bindings: [] };
    altMap[key].bindings.push(b);
  });
  const alternatives = Object.values(altMap).sort((a, b) => a.alternativeNo - b.alternativeNo);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`lane-${laneKey.replace(/\./g, "-")}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-700">{label}</span>
          <span className="text-[10px] text-slate-400 font-mono">{laneKey}</span>
          {v1Url && <span className="text-[10px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded">V1</span>}
          {bindings.length > 0 && (
            <span className="text-[10px] bg-blue-100 text-blue-600 px-1 py-0.5 rounded">{bindings.length} V2</span>
          )}
          {activeSelectionId && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">Seçili</span>}
        </div>
        {expanded ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 space-y-3 bg-slate-50/40">
          <div className="pt-3 space-y-3">
            {/* V1 Legacy */}
            {v1Url && (
              <V1LegacyCard url={v1Url} mediaType={v1MediaType} label={label} />
            )}

            {/* V2 Alternatifleri */}
            {alternatives.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">V2 Alternatifleri</p>
                {alternatives.map(alt => (
                  <AlternativeGroup
                    key={alt.alternativeId}
                    alternativeNo={alt.alternativeNo}
                    alternativeId={alt.alternativeId}
                    bindings={alt.bindings}
                    assetMap={assetMap}
                    activeSelectionId={activeSelectionId}
                    onSelectBinding={onSelectBinding}
                    canPublish={canPublish}
                  />
                ))}
              </div>
            )}

            {!hasContent && (
              <p className="text-xs text-slate-400 py-2 text-center">Bu lane için henüz medya yok</p>
            )}

            {/* Upload / External URL Butonu */}
            {canUpload && (
              <button
                type="button"
                onClick={() => onUpload(laneKey)}
                className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-blue-300 rounded-lg text-xs text-blue-600 hover:bg-blue-50 transition-colors"
                data-testid={`upload-btn-${laneKey.replace(/\./g, "-")}`}
              >
                <Upload size={12} /> Yeni Medya Yükle
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Compact: Binding Satırı ──────────────────────────────────────────────────
function CompactBindingRow({ binding, asset, isSelected, onTryBinding }) {
  const url = asset?.playback_url || asset?.external_url;
  return (
    <div
      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded ${isSelected ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-slate-100'}`}
      data-testid={`compact-binding-${binding.binding_id}`}
    >
      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
        <span className="text-[10px] font-mono text-slate-500 shrink-0">Rev.{binding.revision_no}</span>
        <StatusBadge status={binding.status} />
        {isSelected && (
          <span className="text-[9px] bg-emerald-600 text-white px-1 py-0.5 rounded font-semibold flex items-center gap-0.5 shrink-0">
            <CheckCircle size={8} />Seçili
          </span>
        )}
        {asset?.media_type && (
          <span className="text-[9px] text-slate-400">{asset.media_type}</span>
        )}
      </div>
      {url && (
        <button
          type="button"
          onClick={() => onTryBinding?.({
            playback_url: url,
            media_type: asset.media_type ?? 'image',
            usage_key: binding.usage_key,
            option_key: binding.option_key ?? null,
            binding_id: binding.binding_id,
          })}
          className="text-[10px] px-1.5 py-1 border border-violet-300 text-violet-700 rounded hover:bg-violet-50 transition-colors shrink-0"
          data-testid={`try-binding-${binding.binding_id}`}
        >
          Dene
        </button>
      )}
    </div>
  );
}

// ─── Compact: Lane Öğesi ──────────────────────────────────────────────────────
function CompactLaneItem({ usageKey, optionKey, label, v1Url, v1MediaType, bindings, assetMap, activeSelectionId, showIfEmpty, onTryBinding }) {
  const [expanded, setExpanded] = useState(false);
  const hasContent = v1Url || bindings.length > 0;
  if (!hasContent && !showIfEmpty) return null;

  const testKey = usageKey.replace(/\./g, '-') + (optionKey ? '-' + optionKey : '');

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`compact-lane-${testKey}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-white hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <span className="text-xs text-slate-700 truncate max-w-[8rem]">{label}</span>
          <span className="text-[9px] text-slate-400 font-mono shrink-0 hidden sm:inline">{usageKey}{optionKey ? '/' + optionKey : ''}</span>
          {v1Url && <span className="text-[9px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded shrink-0">V1</span>}
          {bindings.length > 0 && <span className="text-[9px] bg-blue-100 text-blue-600 px-1 py-0.5 rounded shrink-0">{bindings.length}×V2</span>}
          {activeSelectionId && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded shrink-0">Seçili</span>}
        </div>
        {expanded ? <ChevronUp size={11} className="text-slate-400 shrink-0" /> : <ChevronDown size={11} className="text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-1.5 border-t border-slate-100 bg-slate-50/30 pt-2">
          {/* V1 Legacy */}
          {v1Url && (
            <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-100 border border-slate-200 rounded">
              <span className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-semibold shrink-0">V1</span>
              <button
                type="button"
                onClick={() => onTryBinding?.({
                  playback_url: v1Url,
                  media_type: v1MediaType,
                  usage_key: usageKey,
                  option_key: optionKey ?? null,
                  binding_id: `v1-${testKey}`,
                })}
                className="text-[10px] px-1.5 py-1 border border-violet-300 text-violet-700 rounded hover:bg-violet-50 transition-colors shrink-0"
                data-testid={`try-v1-${testKey}`}
              >
                Dene
              </button>
            </div>
          )}

          {/* V2 Bindings */}
          {bindings.map(b => (
            <CompactBindingRow
              key={b.binding_id}
              binding={b}
              asset={assetMap[b.asset_id]}
              isSelected={b.binding_id === activeSelectionId}
              onTryBinding={onTryBinding}
            />
          ))}

          {!hasContent && (
            <p className="text-[10px] text-slate-400 py-1 text-center italic">Henüz medya yok</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Compact: Grup Bölümü ─────────────────────────────────────────────────────
function CompactGroupSection({ group, bindings, assetMap, activeSelections, questionData, showAllLanes, onTryBinding }) {
  const [expanded, setExpanded] = useState(true);

  const lanesWithContent = group.lanes.filter(uk => {
    const lane = LANE_BY_KEY[uk];
    const v1Url = lane?.v1Field ? questionData?.[lane.v1Field] : null;
    const lb = bindings.filter(b => b.usage_key === uk && !b.option_key);
    return v1Url || lb.length > 0;
  });

  if (lanesWithContent.length === 0 && !showAllLanes) return null;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`compact-group-${group.id}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-700">{group.label}</span>
          {lanesWithContent.length > 0 && (
            <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{lanesWithContent.length}</span>
          )}
        </div>
        {expanded ? <ChevronUp size={11} className="text-slate-400" /> : <ChevronDown size={11} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-slate-100 space-y-1.5 pt-2 bg-white">
          {group.lanes.map(uk => {
            const lane = LANE_BY_KEY[uk];
            if (!lane) return null;
            const lb = bindings.filter(b => b.usage_key === uk && !b.option_key);
            const v1Url = lane.v1Field ? questionData?.[lane.v1Field] : null;
            const hasContent = v1Url || lb.length > 0;
            if (!hasContent && !showAllLanes) return null;
            return (
              <CompactLaneItem
                key={uk}
                usageKey={uk}
                optionKey={null}
                label={lane.label}
                v1Url={v1Url}
                v1MediaType={lane.mediaIcon}
                bindings={lb}
                assetMap={assetMap}
                activeSelectionId={activeSelections[uk]}
                showIfEmpty={showAllLanes}
                onTryBinding={onTryBinding}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Compact: Şıklar Grubu ────────────────────────────────────────────────────
function CompactOptionsGroup({ bindings, assetMap, activeSelections, questionData, showAllLanes, onTryBinding }) {
  const [expanded, setExpanded] = useState(true);

  const optKeysWithContent = OPTION_KEYS.filter(optKey => {
    const v1Url = questionData?.[`option_${optKey.toLowerCase()}_image_url`];
    const lbVisual = bindings.filter(b => b.usage_key === 'option.visual' && b.option_key === optKey);
    const lbFeedback = bindings.filter(b => b.usage_key === 'option.feedback' && b.option_key === optKey);
    return v1Url || lbVisual.length > 0 || lbFeedback.length > 0;
  });

  if (optKeysWithContent.length === 0 && !showAllLanes) return null;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="compact-group-options">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-700">Şıklar</span>
          {optKeysWithContent.length > 0 && (
            <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{optKeysWithContent.length}</span>
          )}
        </div>
        {expanded ? <ChevronUp size={11} className="text-slate-400" /> : <ChevronDown size={11} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-slate-100 space-y-1.5 pt-2 bg-white">
          {OPTION_KEYS.map(optKey => {
            const v1Url = questionData?.[`option_${optKey.toLowerCase()}_image_url`];
            const lb = bindings.filter(b => b.usage_key === 'option.visual' && b.option_key === optKey);
            const hasContent = v1Url || lb.length > 0;
            if (!hasContent && !showAllLanes) return null;
            return (
              <CompactLaneItem
                key={optKey}
                usageKey="option.visual"
                optionKey={optKey}
                label={`Şık ${optKey} Görseli`}
                v1Url={v1Url}
                v1MediaType="image"
                bindings={lb}
                assetMap={assetMap}
                activeSelectionId={activeSelections[`option.visual__${optKey}`]}
                showIfEmpty={showAllLanes}
                onTryBinding={onTryBinding}
              />
            );
          })}
          {/* Şık bazlı geri bildirim lane'leri — option.feedback/A-D */}
          {OPTION_KEYS.map(optKey => {
            const lb = bindings.filter(b => b.usage_key === 'option.feedback' && b.option_key === optKey);
            if (lb.length === 0 && !showAllLanes) return null;
            return (
              <CompactLaneItem
                key={`fb-${optKey}`}
                usageKey="option.feedback"
                optionKey={optKey}
                label={`Şık ${optKey} Geri Bildirimi`}
                v1Url={null}
                v1MediaType="video"
                bindings={lb}
                assetMap={assetMap}
                activeSelectionId={activeSelections[`option.feedback__${optKey}`]}
                showIfEmpty={showAllLanes}
                onTryBinding={onTryBinding}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Compact: Ana Panel ───────────────────────────────────────────────────────
function CompactVaultPanel({ bindings, assetMap, activeSelections, qualityWarnings, questionData, capabilities, loadingBindings, onRefresh, onTryBinding }) {
  const [showAllLanes, setShowAllLanes] = useState(false);
  const masterOn = capabilities?.gates?.master === true;
  const writeOn  = capabilities?.gates?.write  === true;

  return (
    <div className="space-y-2" data-testid="compact-vault-panel">
      {/* Minimal başlık satırı */}
      <div className="flex items-center justify-between pb-0.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Medya V2</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5 ${masterOn ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
            {masterOn ? <CheckCircle size={8} /> : <Lock size={8} />} Master
          </span>
          {masterOn && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${writeOn ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-600'}`}>
              {writeOn ? 'Yazma' : 'Salt-ok'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowAllLanes(v => !v)}
            className={`text-[10px] px-2 py-1 rounded border transition-colors ${showAllLanes ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
            data-testid="show-all-lanes-btn"
          >
            {showAllLanes ? 'Dolu hedefler' : 'Tüm hedefler'}
          </button>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loadingBindings}
            className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100"
            data-testid="compact-refresh-btn"
          >
            <RefreshCw size={12} className={loadingBindings ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Kalite uyarıları (kompakt) */}
      {qualityWarnings.length > 0 && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <AlertTriangle size={11} className="text-amber-500 shrink-0" />
          <span className="text-[10px] font-semibold text-amber-700">{qualityWarnings.length} kalite uyarısı</span>
        </div>
      )}

      {/* Tematik gruplar */}
      {COMPACT_GROUPS.map(group => (
        <CompactGroupSection
          key={group.id}
          group={group}
          bindings={bindings}
          assetMap={assetMap}
          activeSelections={activeSelections}
          questionData={questionData}
          showAllLanes={showAllLanes}
          onTryBinding={onTryBinding}
        />
      ))}

      {/* Şıklar özel grubu */}
      <CompactOptionsGroup
        bindings={bindings}
        assetMap={assetMap}
        activeSelections={activeSelections}
        questionData={questionData}
        showAllLanes={showAllLanes}
        onTryBinding={onTryBinding}
      />
    </div>
  );
}

// ─── Galeri Kartı ─────────────────────────────────────────────────────────────
function GalleryCard({ item, onSelectBinding, canPublish }) {
  const [playing, setPlaying] = useState(false);
  const isVideo = item.mediaType === "video" || item.mediaType === "animation";
  const isAudio = item.mediaType === "audio";
  const isImage = item.mediaType === "image" || item.mediaType === "gif";

  const borderCls = item.isSelected
    ? "border-emerald-400 ring-2 ring-emerald-200"
    : "border-slate-200";

  return (
    <div
      className={`rounded-xl border-2 overflow-hidden flex flex-col bg-white ${borderCls}`}
      data-testid={`gallery-card-${item.id}`}
    >
      {/* Medya Alanı */}
      <div className="relative bg-slate-100 flex items-center justify-center" style={{ aspectRatio: "16/9" }}>
        {isImage && (
          <img
            src={item.url}
            alt={item.label}
            className="w-full h-full object-contain"
            onError={e => { e.currentTarget.style.display = "none"; }}
          />
        )}
        {isVideo && !playing && (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="flex flex-col items-center gap-1.5 text-slate-400 hover:text-blue-600 transition-colors"
            data-testid={`gallery-play-${item.id}`}
          >
            <div className="w-11 h-11 rounded-full bg-white/90 shadow-md flex items-center justify-center">
              <Play size={20} className="ml-0.5" />
            </div>
            <span className="text-[10px] font-medium">Oynat</span>
          </button>
        )}
        {isVideo && playing && (
          <video
            src={item.url}
            controls
            autoPlay
            playsInline
            muted
            className="w-full h-full object-contain"
          />
        )}
        {isAudio && !playing && (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="flex flex-col items-center gap-1.5 text-slate-400 hover:text-blue-600 transition-colors"
            data-testid={`gallery-play-${item.id}`}
          >
            <div className="w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center">
              <Volume2 size={17} />
            </div>
            <span className="text-[10px] font-medium">Oynat</span>
          </button>
        )}
        {isAudio && playing && (
          <div className="w-full px-3 py-4">
            <audio src={item.url} controls autoPlay className="w-full" />
          </div>
        )}
        {!isImage && !isVideo && !isAudio && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-500 underline p-3 text-center"
          >
            {item.label}
          </a>
        )}
        {/* Rozetler */}
        <div className="absolute top-1.5 left-1.5 flex flex-wrap gap-0.5">
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${item.tag === "V2" ? "bg-blue-600 text-white" : "bg-slate-600 text-white"}`}>
            {item.tag}
          </span>
          {item.isSelected && (
            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-emerald-600 text-white flex items-center gap-0.5">
              <CheckCircle size={8} /> Seçili
            </span>
          )}
        </div>
      </div>
      {/* Alt Bilgi */}
      <div className="px-2 py-1.5 border-t border-slate-100 flex items-center justify-between gap-1 min-h-[2rem]">
        <p className="text-[10px] text-slate-600 truncate flex-1 min-w-0" title={item.label}>{item.label}</p>
        {item.isV2 && !item.isSelected && item.binding?.status === "active" && canPublish && (
          <button
            type="button"
            onClick={() => onSelectBinding?.(item.binding)}
            className="text-[9px] px-1.5 py-0.5 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 transition-colors shrink-0"
            data-testid={`gallery-select-${item.id}`}
          >
            Seç
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Medya Galerisi (Tam Kasa birincil görünüm) ───────────────────────────────
function MediaGridGallery({ bindings, assetMap, activeSelections, questionData, capabilities, onSelectBinding }) {
  const canPublish = capabilities?.my_permissions?.publish_question_media === true
    && capabilities?.gates?.write === true
    && capabilities?.gates?.master === true;

  const items = [];

  // V1 soru lane'leri
  QUESTION_LANES.forEach(lane => {
    const url = lane.v1Field ? questionData?.[lane.v1Field] : null;
    if (!url) return;
    items.push({
      id: `v1-${lane.usageKey.replace(/\./g, "-")}`,
      url, mediaType: lane.mediaIcon, label: lane.label,
      tag: "V1", isV2: false, isSelected: false, binding: null,
    });
  });

  // V1 şık görselleri
  OPTION_KEYS.forEach(optKey => {
    const url = questionData?.[`option_${optKey.toLowerCase()}_image_url`];
    if (!url) return;
    items.push({
      id: `v1-opt-${optKey}`,
      url, mediaType: "image", label: `Şık ${optKey} Görseli`,
      tag: "V1", isV2: false, isSelected: false, binding: null,
    });
  });

  // V2 tüm alternatifler + tüm revizyonlar
  bindings.forEach(b => {
    const asset = assetMap[b.asset_id];
    if (!asset) return;
    const url = asset.playback_url || asset.external_url;
    if (!url) return;
    const selKey = b.option_key ? `${b.usage_key}__${b.option_key}` : b.usage_key;
    const isSelected = activeSelections[selKey] === b.binding_id;
    const laneLabel = LANE_BY_KEY[b.usage_key]?.label || b.usage_key;
    const altRev = `Alt.${b.alternative_no ?? "?"} Rev.${b.revision_no ?? "?"}`;
    const label = b.option_key
      ? `${laneLabel}/${b.option_key} — ${altRev}`
      : `${laneLabel} — ${altRev}`;
    items.push({
      id: `v2-${b.binding_id}`,
      url, mediaType: asset.media_type, label,
      tag: "V2", isV2: true, isSelected, binding: b,
    });
  });

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="media-grid-gallery">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
        Medya Galerisi
        {items.length > 0 && (
          <span className="normal-case font-normal text-slate-300 ml-1">({items.length})</span>
        )}
      </p>
      {items.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-xs border border-dashed border-slate-200 rounded-lg" data-testid="media-grid-gallery-empty">
          Henüz medya atanmamış
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {items.map(item => (
            <GalleryCard
              key={item.id}
              item={item}
              onSelectBinding={onSelectBinding}
              canPublish={canPublish}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Upload / External URL Modalı ────────────────────────────────────────────
// Tam akış: create-session → XHR PUT (exact Content-Type) → complete → gallery refresh
// Presigned URL hiçbir zaman DOM'a veya console'a yazılmaz.
function UploadModal({ questionId, initialUsageKey = "", onClose, onSuccess }) {
  const [mode, setMode] = useState("file"); // "file" | "url"
  const [usageKey, setUsageKey] = useState(initialUsageKey || "question.primary_visual");
  const [optionKey, setOptionKey] = useState("");
  const [orientation, setOrientation] = useState("original");
  const [language, setLanguage] = useState("tr");
  const [intent, setIntent] = useState("new_alternative");
  const [alternativeId, setAlternativeId] = useState("");
  const [mediaType, setMediaType] = useState("image");
  const [mimeType, setMimeType] = useState("image/jpeg");
  const [title, setTitle] = useState("");
  const [label, setLabel] = useState("");
  const [sha256, setSha256] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [step, setStep] = useState("form"); // "form" | "done"
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const xhrRef = useRef(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  // uploadPhase: "idle" | "creating" | "uploading" | "verifying" | "error"
  const [uploadPhase, setUploadPhase] = useState("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressComputable, setProgressComputable] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // option.visual ve option.feedback her ikisi için option_key zorunludur
  const isOption = usageKey === "option.visual" || usageKey === "option.feedback";
  const inProgress = uploadPhase === "creating" || uploadPhase === "uploading" || uploadPhase === "verifying";

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    if (file.type) setMimeType(file.type);
  };

  // ── Ana upload akışı: create-session → XHR PUT → complete ──────────────────
  const handleStartUpload = async () => {
    if (mode === "url") {
      setUploadPhase("creating");
      setUploadError(null);
      try {
        const res = await api.post("/v2/media/assets/external-url", {
          question_id: questionId,
          usage_key: usageKey,
          option_key: isOption ? optionKey || undefined : undefined,
          orientation, language, intent,
          alternative_id: intent === "new_revision" ? alternativeId : undefined,
          media_type: mediaType, mime_type: mimeType, external_url: externalUrl,
          title: title || undefined, label: label || undefined,
        });
        toast.success("Dış URL kaydedildi");
        onSuccess(res.data);
        onClose();
      } catch (e) {
        const err = e.response?.data?.error;
        const isWriteDisabled = err?.code === "MEDIA_V2_WRITE_DISABLED" || e.response?.status === 503;
        const errMsg = isWriteDisabled
          ? "Yükleme şu an salt-okunur modda (MEDIA_V2_WRITE_ENABLED=0)"
          : (err?.message || err?.code || "İşlem başarısız");
        toast.error(errMsg);
        setUploadError(errMsg);
        setUploadPhase("error");
      }
      return;
    }

    // ── Dosya modu ──────────────────────────────────────────────────────────
    if (!selectedFile) {
      toast.error("Lütfen önce bir dosya seçin");
      return;
    }

    // 1. Session oluştur
    setUploadPhase("creating");
    setUploadError(null);
    let session;
    try {
      const res = await api.post("/v2/media/upload-sessions", {
        question_id: questionId,
        usage_key: usageKey,
        option_key: isOption ? optionKey || undefined : undefined,
        orientation, language, intent,
        alternative_id: intent === "new_revision" ? alternativeId : undefined,
        media_type: mediaType,
        mime_type: selectedFile.type || mimeType,
        file_size: selectedFile.size,
        sha256: sha256 || undefined,
        title: title || undefined, label: label || undefined,
      });
      session = res.data;
      setCurrentSessionId(session.session_id);
    } catch (e) {
      const err = e.response?.data?.error;
      const isWriteDisabled = err?.code === "MEDIA_V2_WRITE_DISABLED" || e.response?.status === 503;
      const errMsg = isWriteDisabled
        ? "Yükleme şu an salt-okunur modda (MEDIA_V2_WRITE_ENABLED=0)"
        : (err?.message || err?.code || "Oturum oluşturulamadı");
      toast.error(errMsg);
      setUploadError(errMsg);
      setUploadPhase("error");
      return;
    }

    // 2. XHR PUT — presigned URL'ye exact Content-Type ile yükle
    //    Presigned URL hiçbir zaman DOM veya log'a yazılmaz.
    setUploadPhase("uploading");
    setUploadProgress(0);
    setProgressComputable(false);

    const putResult = await new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setProgressComputable(true);
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
        // Total bilinmiyorsa sahte yüzde yok — "Yükleniyor..." gösterilir
      };

      xhr.onload = () => {
        xhrRef.current = null;
        resolve(xhr.status >= 200 && xhr.status < 300
          ? { ok: true }
          : { ok: false, error: `PUT başarısız: HTTP ${xhr.status}` });
      };
      xhr.onerror  = () => { xhrRef.current = null; resolve({ ok: false, error: "Ağ hatası: R2'ye bağlanılamadı" }); };
      xhr.ontimeout = () => { xhrRef.current = null; resolve({ ok: false, error: "Zaman aşımı: Yükleme çok uzun sürdü" }); };
      xhr.onabort  = () => { xhrRef.current = null; resolve({ ok: false, error: "CANCELLED" }); };

      xhr.open("PUT", session.presigned_url);
      xhr.setRequestHeader("Content-Type", selectedFile.type || mimeType);
      xhr.send(selectedFile); // Gerçek File nesnesi gönderilir
    });

    if (!putResult.ok) {
      if (putResult.error !== "CANCELLED") {
        setUploadError(putResult.error);
        setUploadPhase("error");
      } else {
        // Kullanıcı iptal etti — handleCancelUpload zaten onClose() çağırır
        setUploadPhase("idle");
      }
      return;
    }

    // PUT başarılı — %100 göster
    setUploadProgress(100);
    setProgressComputable(true);

    // 3. Complete — yalnızca PUT 2xx sonrası çağrılır
    setUploadPhase("verifying");
    try {
      const res = await api.post(`/v2/media/upload-sessions/${session.session_id}/complete`, {
        sha256: sha256 || undefined,
        actual_size: selectedFile.size,
      });
      toast.success("Upload tamamlandı, asset ve binding oluşturuldu");
      onSuccess(res.data);
      setStep("done");
      setUploadPhase("idle");
    } catch (e) {
      const err = e.response?.data?.error;
      const errMsg = `Dosya yüklendi fakat doğrulama başarısız: ${err?.message || err?.code || "complete hatası"}`;
      setUploadError(errMsg);
      setUploadPhase("error");
    }
  };

  // ── İptal: XHR abort + session cancel endpoint ─────────────────────────────
  const handleCancelUpload = async () => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    if (currentSessionId) {
      try { await api.delete(`/v2/media/upload-sessions/${currentSessionId}`); } catch { /* ignore */ }
      setCurrentSessionId(null);
    }
    setUploadPhase("idle");
    setUploadError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="upload-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-800">
            {step === "done" ? "Tamamlandı"
              : uploadPhase === "creating"  ? "Oturum Oluşturuluyor..."
              : uploadPhase === "uploading" ? "Yükleniyor..."
              : uploadPhase === "verifying" ? "Doğrulanıyor..."
              : uploadPhase === "error"     ? "Hata"
              : "Yeni Medya Yükle"}
          </h2>
          <button
            type="button"
            onClick={inProgress ? undefined : onClose}
            className={`${inProgress ? "opacity-30 cursor-not-allowed" : "hover:text-slate-600"} text-slate-400`}
            data-testid="modal-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">

          {/* ── FORM (idle) ── */}
          {step === "form" && uploadPhase === "idle" && (
            <>
              <div className="flex gap-2">
                <button type="button" onClick={() => setMode("file")}
                  className={`flex-1 py-2 text-xs rounded-lg border transition-colors ${mode === "file" ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                  data-testid="mode-file-btn">
                  <Upload size={12} className="inline mr-1" /> Dosya Yükle
                </button>
                <button type="button" onClick={() => setMode("url")}
                  className={`flex-1 py-2 text-xs rounded-lg border transition-colors ${mode === "url" ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                  data-testid="mode-url-btn">
                  <Link size={12} className="inline mr-1" /> Dış URL
                </button>
              </div>

              <div>
                <Label className="text-xs text-slate-600">Kullanım Türü</Label>
                <select value={usageKey} onChange={e => setUsageKey(e.target.value)}
                  className="w-full mt-1 h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
                  data-testid="usage-key-select">
                  {USAGE_KEY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {isOption && (
                <div>
                  <Label className="text-xs text-slate-600">Şık Hedefi</Label>
                  <select value={optionKey} onChange={e => setOptionKey(e.target.value)}
                    className="w-full mt-1 h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
                    data-testid="option-key-select">
                    <option value="">— Seçin —</option>
                    {OPTION_KEYS.map(k => <option key={k} value={k}>Şık {k}</option>)}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Yönlendirme</Label>
                  <select value={orientation} onChange={e => setOrientation(e.target.value)}
                    className="w-full mt-1 h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
                    data-testid="orientation-select">
                    {ORIENTATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Dil</Label>
                  <Input value={language} onChange={e => setLanguage(e.target.value)}
                    className="mt-1 h-8 text-xs" placeholder="tr" data-testid="language-input" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-600">Amaç</Label>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => setIntent("new_alternative")}
                    className={`flex-1 py-2 text-xs rounded-lg border transition-colors ${intent === "new_alternative" ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}
                    data-testid="intent-new-alternative">
                    Yeni Alternatif
                  </button>
                  <button type="button" onClick={() => setIntent("new_revision")}
                    className={`flex-1 py-2 text-xs rounded-lg border transition-colors ${intent === "new_revision" ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}
                    data-testid="intent-new-revision">
                    Yeni Revizyon
                  </button>
                </div>
              </div>

              {intent === "new_revision" && (
                <div>
                  <Label className="text-xs text-slate-600">Alternative ID <span className="text-red-500">*</span></Label>
                  <Input value={alternativeId} onChange={e => setAlternativeId(e.target.value)}
                    className="mt-1 h-8 text-xs font-mono" placeholder="uuid" data-testid="alternative-id-input" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Medya Tipi</Label>
                  <select value={mediaType} onChange={e => setMediaType(e.target.value)}
                    className="w-full mt-1 h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
                    data-testid="media-type-select">
                    {MEDIA_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">MIME Tipi</Label>
                  <Input value={mimeType} onChange={e => setMimeType(e.target.value)}
                    className="mt-1 h-8 text-xs" placeholder="image/jpeg" data-testid="mime-type-input" />
                </div>
              </div>

              {mode === "url" && (
                <div>
                  <Label className="text-xs text-slate-600">HTTPS URL <span className="text-red-500">*</span></Label>
                  <Input value={externalUrl} onChange={e => setExternalUrl(e.target.value)}
                    className="mt-1 h-8 text-xs font-mono" placeholder="https://..." data-testid="external-url-input" />
                  <p className="text-[10px] text-slate-400 mt-1">Sunucu fetch yapmaz; URL yalnızca kaydedilir.</p>
                </div>
              )}

              {mode === "file" && (
                <div>
                  <Label className="text-xs text-slate-600">Dosya</Label>
                  <div
                    className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-3 hover:border-blue-300 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="file-drop-zone"
                  >
                    {selectedFile ? (
                      <div className="text-xs text-slate-700">
                        <p className="font-medium truncate" data-testid="selected-file-name">{selectedFile.name}</p>
                        <p className="text-slate-400 mt-0.5">
                          {(selectedFile.size / 1024).toFixed(1)} KB · {selectedFile.type || mimeType}
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 text-center">Dosya seçmek için tıklayın</p>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="file-upload-input"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Başlık</Label>
                  <Input value={title} onChange={e => setTitle(e.target.value)}
                    className="mt-1 h-8 text-xs" placeholder="İsteğe bağlı" data-testid="title-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Etiket</Label>
                  <Input value={label} onChange={e => setLabel(e.target.value)}
                    className="mt-1 h-8 text-xs" placeholder="İsteğe bağlı" data-testid="label-input" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-600">SHA-256 (opsiyonel)</Label>
                <Input value={sha256} onChange={e => setSha256(e.target.value)}
                  className="mt-1 h-8 text-xs font-mono" placeholder="Dosya hash'i (doğrulama için)"
                  data-testid="sha256-input" />
                <p className="text-[10px] text-slate-400 mt-1">Checksum sağlarsanız tamamlama adımında doğrulanır.</p>
              </div>

              <button
                type="button"
                onClick={handleStartUpload}
                className="w-full py-2.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                data-testid="create-session-btn"
              >
                {mode === "url" ? "URL Kaydet" : "Yüklemeyi Başlat"}
              </button>
            </>
          )}

          {/* ── UPLOAD İN PROGRESS ── */}
          {step === "form" && inProgress && (
            <div className="space-y-4 py-2" data-testid="upload-progress-panel">
              {uploadPhase === "creating" && (
                <div className="flex items-center gap-3 justify-center py-4">
                  <RefreshCw size={16} className="animate-spin text-blue-500" />
                  <span className="text-sm text-slate-600">Oturum oluşturuluyor...</span>
                </div>
              )}

              {uploadPhase === "uploading" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Yükleniyor...</span>
                    {progressComputable && (
                      <span className="text-xs text-blue-600 font-mono" data-testid="upload-progress-pct">
                        {uploadProgress}%
                      </span>
                    )}
                  </div>
                  {progressComputable ? (
                    <div className="w-full bg-slate-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-500 h-2.5 rounded-full transition-all duration-200"
                        style={{ width: `${uploadProgress}%` }}
                        data-testid="upload-progress-bar"
                      />
                    </div>
                  ) : (
                    <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                      <div className="bg-blue-400 h-2.5 animate-pulse w-full" />
                    </div>
                  )}
                  {selectedFile && (
                    <p className="text-[10px] text-slate-400 text-center">
                      {selectedFile.name} · {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleCancelUpload}
                    className="w-full py-2 border border-red-200 text-red-500 text-xs rounded-lg hover:bg-red-50 transition-colors"
                    data-testid="cancel-upload-btn"
                  >
                    İptal Et
                  </button>
                </div>
              )}

              {uploadPhase === "verifying" && (
                <div className="space-y-3">
                  <div className="w-full bg-slate-200 rounded-full h-2.5">
                    <div className="bg-emerald-500 h-2.5 rounded-full w-full" data-testid="upload-progress-bar" />
                  </div>
                  <div className="flex items-center gap-3 justify-center">
                    <RefreshCw size={16} className="animate-spin text-emerald-500" />
                    <span className="text-sm text-slate-600">Doğrulanıyor...</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── HATA ── */}
          {step === "form" && uploadPhase === "error" && (
            <div className="space-y-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="upload-error-msg">
                <p className="text-xs text-red-700">{uploadError}</p>
              </div>
              <button
                type="button"
                onClick={() => { setUploadPhase("idle"); setUploadError(null); setCurrentSessionId(null); }}
                className="w-full py-2 border border-slate-200 text-xs text-slate-600 rounded-lg hover:bg-slate-50"
                data-testid="retry-upload-btn"
              >
                Tekrar Dene
              </button>
              <button type="button" onClick={onClose}
                className="w-full py-1.5 text-xs text-slate-400 hover:underline">
                Kapat
              </button>
            </div>
          )}

          {/* ── DONE ── */}
          {step === "done" && (
            <div className="text-center py-6 space-y-3">
              <CheckCircle size={32} className="text-emerald-500 mx-auto" />
              <p className="text-sm font-semibold text-slate-700">Asset ve Binding Oluşturuldu</p>
              <p className="text-xs text-slate-500">Bu medya yayına alınmamıştır. Yayın seçimini güncellemek için lane'den seçim yapın.</p>
              <button type="button" onClick={onClose}
                className="px-6 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">
                Kapat
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Yayın Seçimi Onay Modalı ─────────────────────────────────────────────────
function PublishConfirmModal({ binding, currentSelectionId, onConfirm, onClose, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="publish-confirm-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">Yayın Seçimini Değiştir</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex gap-2">
              <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-800">Önemli Not</p>
                <p className="text-xs text-amber-700 mt-1">
                  Bu işlem <strong>Media V2 seçimini</strong> değiştirir. V1 yayın sistemi bu seçimden
                  <strong> etkilenmez</strong> — V1 → V2 yönlendirmesi ayrı bir aşamada (Phase 3F)
                  etkinleştirilecektir.
                </p>
              </div>
            </div>
          </div>
          <div className="text-xs text-slate-600 space-y-1">
            <p>Seçilecek binding: <span className="font-mono text-blue-600">{binding?.binding_id}</span></p>
            <p>Usage key: <span className="font-mono">{binding?.usage_key}</span></p>
            {binding?.alternative_no && <p>Alternatif No: {binding.alternative_no}</p>}
            {binding?.revision_no && <p>Revizyon No: {binding.revision_no}</p>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onConfirm(binding)}
              disabled={loading}
              className="flex-1 py-2 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="confirm-publish-btn"
            >
              {loading ? "Kaydediliyor..." : "V2 Seçimini Onayla"}
            </button>
            <button type="button" onClick={onClose} className="py-2 px-4 border border-slate-200 text-xs rounded-lg hover:bg-slate-50">
              Vazgeç
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────
export default function MediaVaultTab({ questionId, questionData, compact = false, onTryBinding = null }) {
  const [capabilities, setCapabilities] = useState(null);
  const [bindings, setBindings] = useState([]);
  const [assetMap, setAssetMap] = useState({});
  const [qualityWarnings, setQualityWarnings] = useState([]);
  const [activeSelections, setActiveSelections] = useState({});
  const [loadingCaps, setLoadingCaps] = useState(true);
  const [loadingBindings, setLoadingBindings] = useState(false);
  const [uploadModal, setUploadModal] = useState(null); // null | usageKey string
  const [publishModal, setPublishModal] = useState(null); // null | binding
  const [publishing, setPublishing] = useState(false);
  const [conflictVersion, setConflictVersion] = useState(null);

  const myPerms = capabilities?.my_permissions || {};
  const canUpload = myPerms.upload_media === true;
  const canPublish = myPerms.publish_question_media === true;
  const masterOn = capabilities?.gates?.master === true;
  const writeOn = capabilities?.gates?.write === true;

  // Capabilities fetch
  useEffect(() => {
    api.get("/v2/media/capabilities")
      .then(r => setCapabilities(r.data))
      .catch(() => setCapabilities({ gates: { master: false, write: false }, my_permissions: {} }))
      .finally(() => setLoadingCaps(false));
  }, []);

  // Bindings + Quality warnings fetch
  const fetchData = useCallback(async () => {
    if (!questionId) return;
    setLoadingBindings(true);
    try {
      const [bRes, qRes] = await Promise.allSettled([
        api.get(`/v2/media/questions/${questionId}/bindings`),
        api.get(`/v2/media/questions/${questionId}/quality-warnings`),
      ]);
      if (bRes.status === "fulfilled") {
        const bList = bRes.value.data.bindings || [];
        setBindings(bList);
        // Asset'leri al
        const aMap = {};
        await Promise.allSettled(bList.map(async b => {
          if (b.asset_id && !aMap[b.asset_id]) {
            try {
              const ar = await api.get(`/v2/media/assets/${b.asset_id}`);
              aMap[b.asset_id] = ar.data.asset;
            } catch { aMap[b.asset_id] = null; }
          }
        }));
        setAssetMap(aMap);
      }
      if (qRes.status === "fulfilled") {
        setQualityWarnings(qRes.value.data.warnings || []);
      }
    } finally {
      setLoadingBindings(false);
    }
  }, [questionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Belirli bir lane için aktif seçimi al
  const fetchSelection = useCallback(async (usageKey, optionKey = null) => {
    if (!questionId || !masterOn) return;
    try {
      const params = { usage_key: usageKey };
      if (optionKey) params.option_key = optionKey;
      const r = await api.get(`/v2/media/questions/${questionId}/selections/resolved`, { params });
      const key = optionKey ? `${usageKey}__${optionKey}` : usageKey;
      setActiveSelections(prev => ({ ...prev, [key]: r.data.selection?.selected_binding_id }));
    } catch { /* seçim yoksa 404 — sorun değil */ }
  }, [questionId, masterOn]);

  // Yayın seçimi değiştirme
  const handlePublishConfirm = async (binding) => {
    setPublishing(true);
    try {
      const key = binding.option_key
        ? `${binding.usage_key}__${binding.option_key}`
        : binding.usage_key;
      const expectedVersion = conflictVersion ?? undefined;
      await api.put(`/v2/media/questions/${questionId}/selections`, {
        usage_key: binding.usage_key,
        option_key: binding.option_key || undefined,
        orientation: binding.orientation || "original",
        language: binding.language || "tr",
        target_key: "global",
        selected_binding_id: binding.binding_id,
        expected_version: expectedVersion,
      });
      setActiveSelections(prev => ({ ...prev, [key]: binding.binding_id }));
      toast.success("V2 yayın seçimi güncellendi (V1 yayını etkilenmedi)");
      setPublishModal(null);
      setConflictVersion(null);
    } catch (e) {
      const err = e.response?.data?.error;
      if (err?.code === "VERSION_CONFLICT") {
        toast.error("Versiyon çakışması — lütfen sayfayı yenileyip tekrar deneyin");
      } else {
        toast.error(err?.message || "Seçim güncellenemedi");
      }
    } finally {
      setPublishing(false);
    }
  };

  if (loadingCaps) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <RefreshCw size={16} className="animate-spin mr-2" /> Yükleniyor...
      </div>
    );
  }

  // ─── Compact mode erken dönüşü (tüm hook'lardan sonra) ─────────────────────
  if (compact) {
    return (
      <CompactVaultPanel
        bindings={bindings}
        assetMap={assetMap}
        activeSelections={activeSelections}
        qualityWarnings={qualityWarnings}
        questionData={questionData}
        capabilities={capabilities}
        loadingBindings={loadingBindings}
        onRefresh={fetchData}
        onTryBinding={onTryBinding}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="media-vault-tab">

      {/* ── Capabilities/Permission Başlığı ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Media V2 Kasası</p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Master",   on: masterOn },
                { label: "Yazma",    on: writeOn },
                { label: "Yükleme",  on: canUpload },
                { label: "Yayınlama",on: canPublish },
              ].map(({ label, on }) => (
                <span key={label} className={`text-[10px] px-2 py-1 rounded-full font-medium flex items-center gap-1 ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {on ? <CheckCircle size={9} /> : <Lock size={9} />} {label}
                </span>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={fetchData}
            disabled={loadingBindings}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded hover:bg-slate-100"
            data-testid="refresh-vault-btn"
          >
            <RefreshCw size={14} className={loadingBindings ? "animate-spin" : ""} />
          </button>
        </div>

        {!masterOn && (
          <div className="mt-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <Lock size={11} /> Media V2 henüz etkinleştirilmemiş (MEDIA_V2_ENABLED=0).
              V1 legacy medya gösterilmektedir.
            </p>
          </div>
        )}
        {masterOn && !writeOn && (
          <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-600 flex items-center gap-1.5">
              <Lock size={11} /> Yazma kapısı kapalı (MEDIA_V2_WRITE_ENABLED=0). Yükleme devre dışı.
            </p>
          </div>
        )}
      </div>

      {/* ── Kalite Uyarıları ── */}
      {qualityWarnings.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-2" data-testid="quality-warnings-panel">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Kalite Uyarıları</p>
          {qualityWarnings.map((w, i) => (
            <div key={i} className={`flex gap-2 p-2 rounded-lg ${w.severity === "warning" ? "bg-amber-50 border border-amber-200" : w.severity === "info" ? "bg-blue-50 border border-blue-200" : "bg-red-50 border border-red-200"}`}
              data-testid={`quality-warning-${w.code}`}>
              <SeverityIcon severity={w.severity} />
              <div>
                <p className="text-[10px] font-semibold text-slate-700">{w.code}</p>
                <p className="text-xs text-slate-600">{w.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Medya Galerisi (birincil / varsayılan görünüm) ── */}
      <MediaGridGallery
        bindings={bindings}
        assetMap={assetMap}
        activeSelections={activeSelections}
        questionData={questionData}
        capabilities={capabilities}
        onSelectBinding={(b) => setPublishModal(b)}
      />

      {/* ── Soru Lane'leri (gelişmiş yönetim) ── */}
      <details className="group">
        <summary className="cursor-pointer list-none">
          <div className="flex items-center gap-2 px-1 py-2 rounded-lg hover:bg-slate-50 transition-colors">
            <ChevronDown size={13} className="text-slate-400 group-open:rotate-180 transition-transform" />
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest select-none">Gelişmiş Lane Yönetimi</p>
          </div>
        </summary>
        <div className="space-y-4 mt-1">

      {/* ── Soru Lane'leri ── */}
      <div className="space-y-2">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest px-1">Soru Medyası</p>
        {QUESTION_LANES.map(lane => {
          const laneBind = bindings.filter(b => b.usage_key === lane.usageKey && !b.option_key);
          const activeSel = activeSelections[lane.usageKey];
          const v1Url = lane.v1Field ? questionData?.[lane.v1Field] : null;
          return (
            <LanePanel
              key={lane.usageKey}
              laneKey={lane.usageKey}
              label={lane.label}
              v1Url={v1Url}
              v1MediaType={lane.mediaIcon}
              bindings={laneBind}
              assetMap={assetMap}
              activeSelectionId={activeSel}
              onSelectBinding={(b) => setPublishModal(b)}
              onUpload={(lk) => setUploadModal(lk)}
              canUpload={canUpload && writeOn && masterOn}
              canPublish={canPublish && writeOn && masterOn}
            />
          );
        })}
      </div>

      {/* ── Şık Görseli Lane'leri (A-D sabit) ── */}
      <div className="space-y-2">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest px-1">Şık Görselleri</p>
        {OPTION_KEYS.map(optKey => {
          const laneBind = bindings.filter(b => b.usage_key === "option.visual" && b.option_key === optKey);
          const selKey = `option.visual__${optKey}`;
          const activeSel = activeSelections[selKey];
          const v1Url = questionData?.[`option_${optKey.toLowerCase()}_image_url`];
          return (
            <LanePanel
              key={optKey}
              laneKey={`option.visual__${optKey}`}
              label={`Şık ${optKey} Görseli`}
              v1Url={v1Url}
              v1MediaType="image"
              bindings={laneBind}
              assetMap={assetMap}
              activeSelectionId={activeSel}
              onSelectBinding={(b) => setPublishModal(b)}
              onUpload={() => {
                setUploadModal("option.visual");
              }}
              canUpload={canUpload && writeOn && masterOn}
              canPublish={canPublish && writeOn && masterOn}
            />
          );
        })}
      </div>

      {/* ── Şık Geri Bildirimi Lane'leri (A-D) — option.feedback ── */}
      <div className="space-y-2">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest px-1">Şık Geri Bildirimleri</p>
        {OPTION_KEYS.map(optKey => {
          const laneBind = bindings.filter(b => b.usage_key === "option.feedback" && b.option_key === optKey);
          const selKey = `option.feedback__${optKey}`;
          const activeSel = activeSelections[selKey];
          return (
            <LanePanel
              key={`fb-${optKey}`}
              laneKey={`option.feedback__${optKey}`}
              label={`Şık ${optKey} Geri Bildirimi`}
              v1Url={null}
              v1MediaType="video"
              bindings={laneBind}
              assetMap={assetMap}
              activeSelectionId={activeSel}
              onSelectBinding={(b) => setPublishModal(b)}
              onUpload={() => setUploadModal("option.feedback")}
              canUpload={canUpload && writeOn && masterOn}
              canPublish={canPublish && writeOn && masterOn}
            />
          );
        })}
      </div>

        </div>{/* /details-content */}
      </details>

      {/* ── Modaller ── */}
      {uploadModal !== null && (
        <UploadModal
          questionId={questionId}
          initialUsageKey={uploadModal}
          onClose={() => setUploadModal(null)}
          onSuccess={() => { fetchData(); }}
        />
      )}

      {publishModal && (
        <PublishConfirmModal
          binding={publishModal}
          onConfirm={handlePublishConfirm}
          onClose={() => { setPublishModal(null); setConflictVersion(null); }}
          loading={publishing}
        />
      )}
    </div>
  );
}
